// FU-POS-6c-1 — client-side send/retry queue for POS mutations.
//
// Target: seconds–minutes connectivity blips (the ratified Q4 depth — NOT full
// local capture). A mutating `/pos/*` request that fails with a NETWORK error
// (fetch rejects) is queued with its STABLE `Idempotency-Key` and replayed FIFO
// on reconnect. The server's idempotency layer (FU-POS-6a/6b, `withIdempotency`)
// makes replay at-most-once safe (a replay returns the stored outcome without
// re-executing — money applies once).
//
// Deliberate boundaries:
//   • HTTP error responses (4xx/5xx) are NOT queued — they are real answers.
//   • Only a genuine fetch rejection (offline / unreachable) enqueues.
//   • Entries are tenant-scoped and never replayed across restaurants.
//   • This slice is the MECHANISM only. Optimistic per-screen "pending" UI is
//     FU-POS-6c-2.

const STORAGE_KEY = 'pos.offline.queue.v1'

/** A mutation captured while offline, to be replayed verbatim on reconnect. */
export interface QueuedRequest {
  /** Queue-entry id — distinct from `key`; identifies the row for removal. */
  id: string
  /** The `Idempotency-Key` — STABLE across every retry (the whole point). */
  key: string
  method: string
  /** `/api`-relative path, including the leading `/pos/...`. */
  path: string
  /** Pre-serialized JSON body (as sent on the first attempt), if any. */
  body?: string
  /** Tenant scope — an entry is only ever replayed for this restaurant. */
  restaurantId: string
  enqueuedAt: number
}

export interface QueueStatus {
  online: boolean
  /** Number of queued entries for the tenant the status was computed for. */
  queued: number
}

/** Outcome of a single replay attempt. */
export type SendOutcome =
  | 'settled' // server returned SOME response (2xx or definitive 4xx/5xx) → drop
  | 'offline' // fetch rejected (still no connectivity) → keep entry, stop draining

export type Sender = (r: QueuedRequest) => Promise<SendOutcome>

type StatusListener = (s: QueueStatus) => void

// A minimal storage shape so the queue is trivially unit-testable with an
// in-memory stand-in (jsdom's localStorage works too).
export type QueueStorage = Pick<Storage, 'getItem' | 'setItem'>

function memoryStorage(): QueueStorage {
  const m = new Map<string, string>()
  return {
    getItem: (k) => {
      const v = m.get(k)
      return v === undefined ? null : v
    },
    setItem: (k, v) => void m.set(k, v),
  }
}

function defaultStorage(): QueueStorage {
  try {
    if (typeof localStorage !== 'undefined') return localStorage
  } catch {
    // localStorage can throw (private mode / disabled) — fall back to memory.
  }
  return memoryStorage()
}

function isOnline(): boolean {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean') {
      return navigator.onLine
    }
  } catch {
    /* ignore */
  }
  return true
}

function newId(): string {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  } catch {
    /* ignore */
  }
  return `q_${Date.now()}_${Math.random().toString(36).slice(2)}`
}

export class OfflineQueue {
  private listeners = new Set<StatusListener>()
  private draining = false

  constructor(private storage: QueueStorage = defaultStorage()) {}

  private read(): QueuedRequest[] {
    const raw = this.storage.getItem(STORAGE_KEY)
    if (!raw) return []
    try {
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? (parsed as QueuedRequest[]) : []
    } catch {
      return [] // corrupt payload — treat as empty rather than crash the app
    }
  }

  private write(list: QueuedRequest[]): void {
    this.storage.setItem(STORAGE_KEY, JSON.stringify(list))
    this.emit()
  }

  /** Enqueue a mutation (called after a network failure). Returns the entry. */
  enqueue(input: Omit<QueuedRequest, 'id' | 'enqueuedAt'>): QueuedRequest {
    const entry: QueuedRequest = { ...input, id: newId(), enqueuedAt: Date.now() }
    this.write([...this.read(), entry])
    return entry
  }

  /** All entries, oldest first. */
  all(): QueuedRequest[] {
    return this.read()
  }

  /** Count of entries pending for a given tenant. */
  sizeFor(restaurantId: string): number {
    return this.read().filter((e) => e.restaurantId === restaurantId).length
  }

  /** Entries pending for a given tenant, oldest first (FU-POS-6c-2: pending UI). */
  entriesFor(restaurantId: string): QueuedRequest[] {
    return this.read().filter((e) => e.restaurantId === restaurantId)
  }

  subscribe(fn: StatusListener, restaurantId?: string): () => void {
    this.listeners.add(fn)
    fn(this.statusFor(restaurantId))
    return () => void this.listeners.delete(fn)
  }

  private statusFor(restaurantId?: string): QueueStatus {
    const list = this.read()
    return {
      online: isOnline(),
      queued: restaurantId ? list.filter((e) => e.restaurantId === restaurantId).length : list.length,
    }
  }

  /** Notify subscribers. `restaurantId` scoping is applied per-listener call. */
  private emit(): void {
    if (this.listeners.size === 0) return
    // Listeners receive an unscoped count; scoped consumers recompute via sizeFor.
    const status = this.statusFor()
    for (const fn of this.listeners) fn(status)
  }

  /**
   * Replay queued entries for one tenant, FIFO, stopping at the first entry that
   * still fails with a network error. Re-entrancy-guarded so overlapping
   * triggers (the `online` event + the poll interval) can't double-send.
   */
  async drain(restaurantId: string, send: Sender): Promise<void> {
    if (this.draining) return
    this.draining = true
    try {
      for (const entry of this.read()) {
        if (entry.restaurantId !== restaurantId) continue // leave other tenants untouched
        const outcome = await send(entry)
        if (outcome === 'offline') break // still down — keep this and the rest, retry later
        this.remove(entry.id) // settled (success OR definitive error): safe to drop
      }
    } finally {
      this.draining = false
      this.emit()
    }
  }

  private remove(id: string): void {
    this.write(this.read().filter((e) => e.id !== id))
  }

  /** Test helper — clear all entries. */
  clear(): void {
    this.write([])
  }
}

/** App-wide singleton (backed by localStorage in the browser). */
export const offlineQueue = new OfflineQueue()

/** Exposed for unit tests that want an isolated in-memory instance. */
export const __test = { memoryStorage }
